import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import ContentTypeSelector from './components/ContentTypeSelector';
import PressReleaseForm from './components/PressReleaseForm';
import SocialMediaForm from './components/SocialMediaForm';
import BlogPostForm from './components/BlogPostForm';
import StoryAnglesForm from './components/StoryAnglesForm';
import ContentPreview from './components/ContentPreview';
import GenerationSettings from './components/GenerationSettings';
import Icon from '../../components/AppIcon';

const AIContentCreator = () => {
  const [selectedContentType, setSelectedContentType] = useState('press-release');
  const [formData, setFormData] = useState({});
  const [generatedContent, setGeneratedContent] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationSettings, setGenerationSettings] = useState({
    creativity: 'medium',
    length: 'medium',
    includeStats: false,
    includeQuotes: false,
    factCheck: true
  });

  // Mock generated content for different types
  const mockContent = {
    'press-release': `FOR IMMEDIATE RELEASE

PenGame Publishing Announces the Launch of "The Digital Revolution" by Sarah Mitchell

New Book Explores the Impact of Technology on Modern Society

NEW YORK, NY - October 31, 2024 - PenGame Publishing is proud to announce the release of "The Digital Revolution: How Technology is Reshaping Our World" by acclaimed author Sarah Mitchell. This groundbreaking work examines the profound ways digital technology has transformed every aspect of modern life, from communication and commerce to education and entertainment.

"The Digital Revolution" offers readers an insightful exploration of how technological advances have created both unprecedented opportunities and significant challenges for individuals and society as a whole. Mitchell, drawing from her extensive research and interviews with leading technology experts, presents a balanced perspective on our digital future.

"Technology has fundamentally changed how we live, work, and connect with one another," said Sarah Mitchell. "This book aims to help readers understand these changes and navigate the digital landscape with confidence and purpose."

The book features compelling case studies, expert interviews, and practical insights that make complex technological concepts accessible to general readers. Mitchell's engaging writing style and thorough research have earned praise from early reviewers and industry professionals.

"The Digital Revolution" is available in hardcover, paperback, and digital formats through major retailers and online platforms. The book is priced at $24.99 for hardcover and $14.99 for digital editions.

About the Author:
Sarah Mitchell is a technology journalist and author with over 15 years of experience covering digital innovation. She has written for major publications including Tech Today, Digital Trends, and Innovation Weekly. This is her third book exploring the intersection of technology and society.

About PenGame Publishing:
Founded in 2010, PenGame Publishing is dedicated to bringing thought-provoking and engaging books to readers worldwide. The company specializes in non-fiction works that explore contemporary issues and emerging trends.

For more information about "The Digital Revolution" or to schedule an interview with Sarah Mitchell, please contact:

Media Relations
PenGame Publishing
Phone: (555) 123-4567
Email: media@pengamepublishing.com
Website: www.pengamepublishing.com

###`,

    'social-media': {
      platforms: [
        {
          platform: 'twitter',
          content: `🚀 Excited to announce the launch of "The Digital Revolution" by @SarahMitchell! 📚\n\nDiscover how technology is reshaping our world in this groundbreaking new book. From AI to social media, explore the digital transformation that's changing everything.\n\n#BookLaunch #DigitalRevolution #TechBooks #NewRelease`,
          characterCount: 267
        },
        {
          platform: 'linkedin',
          content: `We're thrilled to announce the publication of "The Digital Revolution: How Technology is Reshaping Our World" by Sarah Mitchell.\n\nThis insightful book examines the profound impact of digital technology on modern society, offering readers a balanced perspective on our technological future.\n\nKey highlights:\n• Expert interviews and case studies\n• Practical insights for navigating digital change\n• Accessible explanations of complex tech concepts\n\nPerfect for professionals, students, and anyone interested in understanding our digital age.\n\nAvailable now in all formats. #DigitalTransformation #Technology #BookLaunch #ProfessionalDevelopment`,
          characterCount: 612
        },
        {
          platform: 'instagram',content: `📖✨ NEW BOOK ALERT! ✨📖\n\n"The Digital Revolution" by Sarah Mitchell is here! 🎉\n\nSwipe to see what makes this book special:\n🔹 Expert insights on tech's impact\n🔹 Real-world case studies\n🔹 Practical guidance for digital life\n🔹 Engaging, accessible writing\n\nWhether you're a tech enthusiast or just curious about our digital future, this book has something for you! 💫\n\nGet your copy today! Link in bio 👆\n\n#BookLaunch #DigitalRevolution #TechBooks #NewRelease #BookLovers #DigitalAge #Innovation #Technology`,
          characterCount: 487
        }
      ]
    },

    'blog-post': {
      title: "5 Ways \'The Digital Revolution\' Will Change How You Think About Technology",
      metaDescription: "Discover how Sarah Mitchell's new book 'The Digital Revolution' offers fresh perspectives on technology's impact on society, work, and daily life.",
      body: `Technology surrounds us every day, but how often do we stop to think about its true impact on our lives? Sarah Mitchell's groundbreaking new book, "The Digital Revolution: How Technology is Reshaping Our World," challenges readers to look beyond the surface and understand the profound ways digital innovation is transforming society.

Here are five key insights from the book that will change how you think about technology:

1. Technology Isn't Just Tools – It's Culture Mitchell argues that we've moved beyond thinking of technology as mere tools. Instead, digital platforms and devices have become integral parts of our cultural fabric. Social media doesn't just help us communicate; it shapes how we form relationships, express identity, and understand the world around us.

2. The Digital Divide Is More Complex Than We Think

While we often discuss the digital divide in terms of access to devices and internet connectivity, Mitchell reveals deeper layers of inequality. The book explores how differences in digital literacy, quality of access, and meaningful use create new forms of social stratification.

3. Privacy Has Been Redefined, Not Destroyed

Rather than lamenting the death of privacy, Mitchell presents a nuanced view of how our concept of privacy has evolved. She examines how younger generations navigate transparency and privacy in ways that might seem contradictory to older perspectives but make perfect sense in a digital context.

4. Automation Creates Opportunities, Not Just Displacement

While job displacement due to automation is a valid concern, Mitchell's research shows how technological advancement also creates new categories of work and opportunities. The key is understanding how to adapt and position ourselves for these emerging roles.

5. Digital Wellness Is the New Literacy

Just as reading and writing became essential skills in the industrial age, Mitchell argues that digital wellness – the ability to maintain healthy relationships with technology – is becoming a crucial life skill. This includes everything from managing screen time to critically evaluating online information.

Why This Matters Now

"The Digital Revolution" arrives at a crucial moment when society is grappling with questions about technology's role in our lives. From AI advancement to social media regulation, the conversations Mitchell starts in her book are more relevant than ever. The book's strength lies in its balanced approach. Rather than taking a purely optimistic or pessimistic view of technology, Mitchell presents evidence-based analysis that helps readers form their own informed opinions.

For anyone looking to better understand our digital age – whether you're a technology professional, educator, parent, or simply a curious citizen – "The Digital Revolution" offers valuable insights that will influence how you interact with technology in your daily life.

Ready to join the conversation? "The Digital Revolution" is available now in hardcover, paperback, and digital formats through all major retailers.`
    },

    'story-angles': {
      angles: [
        {
          title: "Local Author\'s Tech Background Influences Digital Revolution Book",
          description: "Focus on Sarah Mitchell's journey from tech journalist to book author, emphasizing her unique insider perspective on the industry she writes about."
        },
        {
          title: "New Book Challenges Common Assumptions About Digital Privacy",
          description: "Highlight Mitchell's contrarian view that privacy hasn't been destroyed but redefined, offering a fresh take on a hot-button issue."
        },
        {
          title: "Publishing House Bets Big on Tech Books as Digital Literacy Becomes Essential",
          description: "Frame the story around PenGame Publishing\'s strategy and the growing market for technology-focused non-fiction."
        },
        {
          title: "Author Argues Digital Wellness Is the New Literacy in Technology Age",
          description: "Position Mitchell as an expert advocating for a new essential skill set, tying into education and workplace training trends."
        }
      ],
      headlines: [
        "Local Tech Journalist Turns Digital Revolution Into Must-Read Book",
        "New Book Reveals Why Privacy Isn't Dead – Just Different",
        "Author: Digital Wellness Is as Important as Reading and Writing",
        "The Digital Revolution: One Woman\'s Mission to Decode Our Tech-Driven World",
        "From Silicon Valley to Bookshelf: Journalist\'s Inside Look at Digital Transformation"
      ]
    }
  };

  const handleGenerate = () => {
    setIsGenerating(true);
    
    // Simulate AI generation delay
    setTimeout(() => {
      setGeneratedContent(mockContent?.[selectedContentType]);
      setIsGenerating(false);
    }, 2000);
  };

  const handleSave = () => {
    // Mock save functionality
    console.log('Saving content...', generatedContent);
    // In a real app, this would save to backend
  };

  const handleExport = () => {
    // Mock export functionality
    const element = document.createElement('a');
    const file = new Blob([typeof generatedContent === 'string' ? generatedContent : JSON.stringify(generatedContent, null, 2)], {
      type: 'text/plain'
    });
    element.href = URL.createObjectURL(file);
    element.download = `${selectedContentType}-${Date.now()}.txt`;
    document.body?.appendChild(element);
    element?.click();
    document.body?.removeChild(element);
  };

  const handleRegenerate = () => {
    handleGenerate();
  };

  const renderForm = () => {
    switch (selectedContentType) {
      case 'press-release':
        return (
          <PressReleaseForm
            formData={formData}
            onFormChange={setFormData}
            onGenerate={handleGenerate}
            isGenerating={isGenerating}
          />
        );
      case 'social-media':
        return (
          <SocialMediaForm
            formData={formData}
            onFormChange={setFormData}
            onGenerate={handleGenerate}
            isGenerating={isGenerating}
          />
        );
      case 'blog-post':
        return (
          <BlogPostForm
            formData={formData}
            onFormChange={setFormData}
            onGenerate={handleGenerate}
            isGenerating={isGenerating}
          />
        );
      case 'story-angles':
        return (
          <StoryAnglesForm
            formData={formData}
            onFormChange={setFormData}
            onGenerate={handleGenerate}
            isGenerating={isGenerating}
          />
        );
      default:
        return null;
    }
  };

  // Reset form data when content type changes
  useEffect(() => {
    setFormData({});
    setGeneratedContent(null);
  }, [selectedContentType]);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <Breadcrumb />
          
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center space-x-3 mb-4">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Icon name="Sparkles" size={24} className="text-primary" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-foreground">AI Content Creator</h1>
                <p className="text-muted-foreground">
                  Generate professional PR materials using artificial intelligence
                </p>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left Panel - Input Section */}
            <div className="space-y-6">
              <div className="bg-card border border-border rounded-lg p-6">
                <ContentTypeSelector
                  selectedType={selectedContentType}
                  onTypeChange={setSelectedContentType}
                />
              </div>

              <div className="bg-card border border-border rounded-lg p-6">
                {renderForm()}
              </div>

              <GenerationSettings
                settings={generationSettings}
                onSettingsChange={setGenerationSettings}
              />
            </div>

            {/* Right Panel - Output Section */}
            <div className="bg-card border border-border rounded-lg p-6">
              <ContentPreview
                content={generatedContent}
                contentType={selectedContentType}
                onSave={handleSave}
                onExport={handleExport}
                onRegenerate={handleRegenerate}
                isGenerating={isGenerating}
              />
            </div>
          </div>

          {/* Quick Actions */}
          <div className="mt-8 bg-card border border-border rounded-lg p-6">
            <h3 className="text-lg font-semibold text-foreground mb-4">Quick Actions</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <button className="flex items-center space-x-3 p-4 border border-border rounded-lg hover:bg-muted transition-colors duration-200">
                <Icon name="FileText" size={20} className="text-primary" />
                <div className="text-left">
                  <p className="font-medium text-foreground">View Templates</p>
                  <p className="text-sm text-muted-foreground">Browse content templates</p>
                </div>
              </button>
              
              <button className="flex items-center space-x-3 p-4 border border-border rounded-lg hover:bg-muted transition-colors duration-200">
                <Icon name="Save" size={20} className="text-primary" />
                <div className="text-left">
                  <p className="font-medium text-foreground">Saved Drafts</p>
                  <p className="text-sm text-muted-foreground">Access your saved content</p>
                </div>
              </button>
              
              <button className="flex items-center space-x-3 p-4 border border-border rounded-lg hover:bg-muted transition-colors duration-200">
                <Icon name="BarChart3" size={20} className="text-primary" />
                <div className="text-left">
                  <p className="font-medium text-foreground">Usage Analytics</p>
                  <p className="text-sm text-muted-foreground">View generation statistics</p>
                </div>
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AIContentCreator;